import { defineStore } from 'pinia'
import type { TaskQueue } from '../pages/reference-designs/types'

export interface NotificationItem {
  id: string
  message: string
  icon: string
  updateTimestamp: Date
  route?: string
  read?: boolean
}

const STORAGE_KEY = 'notifications'
const MAX_NOTIFICATIONS = 100

export const useNotificationStore = defineStore('notificationStore', {
  state: () => ({
    notifications: [] as NotificationItem[],
    displayAll: false,
    baseVisibleCount: 4,
  }),

  getters: {
    sortedNotifications(state) {
      return [...state.notifications].sort((a, b) => b.updateTimestamp.getTime() - a.updateTimestamp.getTime())
    },
    visibleNotifications(state): NotificationItem[] {
      return state.displayAll
        ? (this.sortedNotifications as NotificationItem[])
        : (this.sortedNotifications as NotificationItem[]).slice(0, state.baseVisibleCount)
    },
    unreadCount(state) {
      return state.notifications.filter((n) => !n.read).length
    },
  },

  actions: {
    loadFromStorage() {
      const data = localStorage.getItem(STORAGE_KEY)
      if (!data) return
      try {
        const parsed: NotificationItem[] = JSON.parse(data)
        this.notifications = parsed.map((n) => ({
          ...n,
          updateTimestamp: new Date(n.updateTimestamp),
        }))
      } catch (err) {
        console.error('Failed to load notifications from localStorage:', err)
      }
    },

    saveToStorage() {
      try {
        if (this.notifications.length > MAX_NOTIFICATIONS) {
          this.notifications = this.notifications
            .sort((a, b) => b.updateTimestamp.getTime() - a.updateTimestamp.getTime())
            .slice(0, MAX_NOTIFICATIONS)
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.notifications))
      } catch (err) {
        console.error('Failed to save notifications:', err)
      }
    },

    toggleDisplayAll() {
      this.displayAll = !this.displayAll
    },

    markAllAsRead() {
      this.notifications = this.notifications.map((n) => ({ ...n, read: true }))
      this.saveToStorage()
    },

    markAsRead(id: string) {
      const notif = this.notifications.find((n) => n.id === id)
      if (notif) notif.read = true
      this.saveToStorage()
    },

    addNotification(item: Omit<NotificationItem, 'updateTimestamp'>) {
      this.notifications.unshift({
        ...item,
        updateTimestamp: new Date(),
      })
      this.saveToStorage()
    },

    addTaskCompletionNotifications(tasks: TaskQueue[]) {
      for (const task of tasks) {
        this.notifications.unshift({
          id: task.id,
          message: `Task queue "${task.name}" has been completed with status: ${task.status}.`,
          icon: 'check_circle',
          updateTimestamp: new Date(),
          route: `/reference-design/${encodeURIComponent(task.ownerId)}#activeTab=taskQueues&taskId=${task.id}`,
          read: false,
        })
      }

      this.saveToStorage()
    },
  },
})
